import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SignaturePad from "@/components/SignaturePad";
import { Drop, CheckCircle, XCircle, SealCheck, CreditCard } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ApproveQuote() {
  const { id } = useParams();
  const [q, setQ] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState(null); // null | "approve" | "decline"
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // "approved" | "declined"
  const sigRef = useRef();

  const load = () => api.publicQuote(id).then(setQ).catch(() => setNotFound(true));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const submitApprove = async () => {
    if (!name.trim()) return toast.error("Please type your full name");
    if (sigRef.current?.isEmpty()) return toast.error("Please sign in the box");
    setBusy(true);
    try {
      await api.approveQuote(id, { signer_name: name.trim(), signature: sigRef.current.toDataURL() });
      setDone("approved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
    finally { setBusy(false); }
  };
  const submitDecline = async () => {
    setBusy(true);
    try { await api.declineQuote(id, reason); setDone("declined"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };
  const payDeposit = async () => {
    setBusy(true);
    try { const { checkout_url } = await api.depositCheckout(id); window.location.href = checkout_url; }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start payment"); setBusy(false); }
  };

  if (notFound) return <Center><p className="text-slate-500">Quote not found.</p></Center>;
  if (!q) return <Center><p className="text-slate-400">Loading quote…</p></Center>;

  const alreadyApproved = q.status === "approved" || q.status === "converted";
  const alreadyDeclined = q.status === "rejected";

  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center justify-center mb-6">
          <img src="/adpw-logo.png" alt="All Day Power Washing" className="h-24 w-auto" />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-[#0B1726] px-6 py-5">
            <div className="text-white font-display font-bold text-lg">Quote {q.number}</div>
            <div className="text-slate-400 text-xs">Prepared for {q.client_name}{q.property_address ? ` · ${q.property_address}` : ""}</div>
          </div>

          <div className="p-6">
            <table className="w-full mb-4">
              <thead><tr className="text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200"><th className="text-left pb-2">Service</th><th className="text-center pb-2">Qty</th><th className="text-right pb-2">Amount</th></tr></thead>
              <tbody>
                {q.line_items.map((li, i) => (
                  <tr key={i} className="border-b border-slate-50"><td className="py-2.5 text-sm text-slate-700">{li.description}</td><td className="py-2.5 text-center text-sm text-slate-500">{li.quantity}</td><td className="py-2.5 text-right text-sm font-medium">{money(li.quantity * li.unit_price)}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="space-y-1.5 mb-2">
              <Row label="Subtotal" value={money(q.subtotal)} />
              {q.tax > 0 && <Row label={`Tax (${q.tax_rate}%)`} value={money(q.tax)} />}
              {q.discount_amount > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">{q.discount_type === "percent" ? `Discount (${q.discount_value}%)` : "Discount"}</span><span className="font-medium text-rose-600">-{money(q.discount_amount)}</span></div>}
              <div className="flex justify-between pt-3 mt-1 border-t-2 border-[#0B1726]"><span className="font-bold text-slate-900">Total</span><span className="font-display font-extrabold text-2xl text-[#00B4D8]">{money(q.total)}</span></div>
              {q.deposit_amount > 0 && (
                <div className="flex justify-between pt-2" data-testid="deposit-line">
                  <span className="text-slate-600 font-semibold">{q.deposit_paid > 0 ? "Deposit Paid" : `Deposit Due (${q.deposit_pct}%)`}</span>
                  <span className={`font-bold ${q.deposit_paid > 0 ? "text-emerald-600" : "text-[#00B4D8]"}`}>{money(q.deposit_paid > 0 ? q.deposit_paid : q.deposit_amount)}</span>
                </div>
              )}
            </div>
            {q.notes && <p className="text-xs text-slate-500 bg-slate-50 rounded-md p-3 mt-3">{q.notes}</p>}

            {/* States */}
            {done === "approved" || alreadyApproved ? (
              <div className="mt-6 text-center bg-emerald-50 border border-emerald-200 rounded-lg p-6" data-testid="approved-state">
                <SealCheck size={48} weight="fill" className="mx-auto text-emerald-500" />
                <h2 className="font-display text-xl font-bold text-slate-900 mt-2">Quote Approved</h2>
                <p className="text-slate-500 text-sm mt-1">Thank you{q.approved_by_name ? `, ${q.approved_by_name}` : ""}! We've received your approval and will be in touch to schedule your service.</p>
                {q.deposit_amount > 0 && q.deposit_paid < q.deposit_amount && (
                  <Button onClick={payDeposit} disabled={busy} className="mt-4 h-11 bg-[#00B4D8] hover:bg-cyan-600 font-semibold" data-testid="pay-deposit-button">
                    <CreditCard size={18} weight="fill" className="mr-1.5" /> {busy ? "Starting…" : `Pay Deposit (${money(q.deposit_amount)})`}
                  </Button>
                )}
                {q.deposit_paid > 0 && <p className="text-emerald-600 text-sm mt-3 font-semibold" data-testid="deposit-paid-note">Deposit of {money(q.deposit_paid)} received — thank you!</p>}
              </div>
            ) : done === "declined" || alreadyDeclined ? (
              <div className="mt-6 text-center bg-slate-50 border border-slate-200 rounded-lg p-6" data-testid="declined-state">
                <XCircle size={48} weight="fill" className="mx-auto text-slate-400" />
                <h2 className="font-display text-xl font-bold text-slate-900 mt-2">Quote Declined</h2>
                <p className="text-slate-500 text-sm mt-1">No problem — we've noted this. Reach out anytime if you'd like to revisit.</p>
              </div>
            ) : mode === "approve" ? (
              <div className="mt-6 space-y-4" data-testid="approve-form">
                <div><Label>Your full name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jane Smith" className="mt-1.5" data-testid="signer-name-input" /></div>
                <div>
                  <Label>Signature</Label>
                  <div className="mt-1.5"><SignaturePad ref={sigRef} /></div>
                  <p className="text-[11px] text-slate-400 mt-1.5">By signing, you approve this quote and authorize the work described above.</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setMode(null)} className="flex-1">Back</Button>
                  <Button onClick={submitApprove} disabled={busy} className="flex-1 bg-[#00B4D8] hover:bg-cyan-600 h-11 font-semibold" data-testid="submit-approval">
                    <CheckCircle size={18} weight="fill" className="mr-1.5" /> {busy ? "Submitting…" : "Approve & Sign"}
                  </Button>
                </div>
              </div>
            ) : mode === "decline" ? (
              <div className="mt-6 space-y-4" data-testid="decline-form">
                <div><Label>Reason (optional)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Let us know why, if you'd like" className="mt-1.5" /></div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setMode(null)} className="flex-1">Back</Button>
                  <Button onClick={submitDecline} disabled={busy} variant="destructive" className="flex-1 h-11" data-testid="submit-decline">{busy ? "Submitting…" : "Decline Quote"}</Button>
                </div>
              </div>
            ) : (
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button onClick={() => setMode("approve")} className="flex-1 h-12 bg-[#00B4D8] hover:bg-cyan-600 text-base font-semibold" data-testid="approve-button">
                  <CheckCircle size={20} weight="fill" className="mr-2" /> Approve Quote
                </Button>
                <Button onClick={() => setMode("decline")} variant="outline" className="flex-1 h-12 text-base" data-testid="decline-button">Decline</Button>
              </div>
            )}
            <p className="text-center text-xs text-slate-400 mt-6">All Day Power Washing · Professional Exterior Cleaning</p>
            <p className="text-center text-xs text-slate-400 mt-1"><a href="tel:+17196446105" className="text-[#00B4D8] hover:underline">(719) 644-6105</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="min-h-screen flex items-center justify-center bg-slate-100">{children}</div>;
const Row = ({ label, value }) => <div className="flex justify-between text-sm"><span className="text-slate-500">{label}</span><span className="font-medium text-slate-700">{value}</span></div>;
